/**
 * Formatting utilities
 */

/**
 * Format number as currency (EUR)
 * @param {number} amount - Amount to format
 * @param {string} locale - Locale for formatting (default: 'it-IT')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, locale = 'it-IT') => {
  if (typeof amount !== 'number' || isNaN(amount)) {
    return '€0';
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  } catch (error) {
    // Fallback for unsupported locales
    return `€${amount.toLocaleString('it-IT')}`;
  }
};

/**
 * Format number with thousands separator
 * @param {number} number - Number to format
 * @param {string} locale - Locale for formatting (default: 'it-IT')
 * @returns {string} Formatted number string
 */
export const formatNumber = (number, locale = 'it-IT') => {
  if (typeof number !== 'number' || isNaN(number)) {
    return '0';
  }

  try {
    return new Intl.NumberFormat(locale).format(number);
  } catch (error) {
    return number.toString();
  }
};

/**
 * Format percentage
 * @param {number} decimal - Decimal value (0.15 for 15%)
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (decimal, decimals = 0) => {
  if (typeof decimal !== 'number' || isNaN(decimal)) {
    return '0%';
  }

  return `${(decimal * 100).toFixed(decimals)}%`;
};

/**
 * Format date in Italian locale
 * @param {Date|string} date - Date to format
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '';

  const dateObj = date instanceof Date ? date : new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    return '';
  }

  const defaultOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options
  };

  try {
    return new Intl.DateTimeFormat('it-IT', defaultOptions).format(dateObj);
  } catch (error) {
    return dateObj.toLocaleDateString('it-IT');
  }
};

/**
 * Format property type for display
 * @param {string} propertyType - Property type key
 * @returns {string} Formatted property type
 */
export const formatPropertyType = (propertyType) => {
  const types = {
    villa: 'Villa',
    apartment: 'Appartamento',
    bnb: 'B&B',
    agriturismo: 'Agriturismo',
    loft: 'Loft',
    studio: 'Monolocale',
    'holiday-home': 'Casa Vacanze'
  };

  return types[propertyType] || propertyType;
};

/**
 * Format period type for display
 * @param {string} periodType - Period type key
 * @returns {string} Formatted period type
 */
export const formatPeriodType = (periodType) => {
  const types = {
    seasonal: 'Stagionale',
    annual: 'Annuale'
  };

  return types[periodType] || periodType;
};

/**
 * Format condition for display
 * @param {string} condition - Condition key
 * @returns {string} Formatted condition
 */
export const formatCondition = (condition) => {
  const conditions = {
    ready: 'Pronta all\'uso',
    furnish: 'Da arredare',
    renovate: 'Da ristrutturare',
    buying: 'In acquisto'
  };

  return conditions[condition] || condition;
};

/**
 * Format services list for display
 * @param {Array<string>} services - Array of service keys
 * @returns {string} Formatted services string
 */
export const formatServices = (services = []) => {
  const serviceLabels = {
    pool: 'Piscina',
    balcony: 'Balcone/Terrazza',
    jacuzzi: 'Jacuzzi',
    garden: 'Giardino/Corte',
    gym: 'Palestra',
    parking: 'Posto auto',
    garage: 'Garage',
    ac: 'Aria condizionata',
    elevator: 'Ascensore',
    view: 'Vista panoramica',
    pets: 'Animali ammessi',
    smartlock: 'Smart lock'
  };

  return services
    .map(service => serviceLabels[service] || service)
    .join(', ');
};

/**
 * Truncate text to specified length
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @param {string} suffix - Suffix to add when truncated (default: '...')
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength, suffix = '...') => {
  if (!text || text.length <= maxLength) {
    return text || '';
  }

  return text.substring(0, maxLength - suffix.length) + suffix;
};

/**
 * Format phone number for display
 * @param {string} phone - Phone number
 * @returns {string} Formatted phone number
 */
export const formatPhone = (phone) => {
  if (!phone) return '';

  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');
  
  // Italian mobile number formatting
  if (cleaned.startsWith('+39') && cleaned.length === 13) {
    return `+39 ${cleaned.slice(3, 6)} ${cleaned.slice(6, 9)} ${cleaned.slice(9)}`;
  }
  
  // Italian landline formatting
  if (cleaned.startsWith('0') && cleaned.length >= 9) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
  }

  return phone; // Return original if no pattern matches
};

/**
 * Capitalize first letter of each word
 * @param {string} text - Text to capitalize
 * @returns {string} Capitalized text
 */
export const capitalizeWords = (text) => {
  if (!text) return '';
  
  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};